<template>
  <div dir="ltr" class="h-screen overflow-hidden flex flex-col">
    <!-- Fixed Header -->
    <div class="h-[12vh] p-[2vw] border-b-2 border-[#E5F1FECC] flex justify-between items-center bg-white shrink-0">
      <div class="flex items-center gap-[2vw] mt-5">
        <span class="font-bold text-[3.5vw]">{{ currentTime }}</span>
      </div>
      <img
          :src="settings.getValue('customers_queue_logo') ? settings.storagePath + '/' + settings.getValue('customers_queue_logo'): '/images/logo.svg'"
          :style="{
            'width': (settings.getValue('customers_queue_logo_size') || 200) + 'px',
            'max-width': '12vw',
            'height': 'auto',
            'margin-top': (settings.getValue('customers_queue_logo_top_margin') || 0) + 'px', 
            'margin-right': (settings.getValue('customers_queue_logo_right_margin') || 0) + 'px'
          }"
          alt=""/>
    </div>

    <!-- Active Customer Section -->
    <div v-if="activeCustomer" class="h-[24vh] flex flex-col justify-center items-end bg-gray-50/30 p-[1.5vw] shrink-0">
      <div class="text-[1.8vw] text-[#45944A] font-bold mb-[0.5vh]">الزبون الحالي</div>
      <div
          :class="['bg-green-200 px-[3vw] py-[0.5vh] text-[4vw] font-bold text-[#45944A] rounded-xl shadow-lg flex items-center justify-center border-2 border-green-300', {'flash-animation': isFlashing}, {'!text-[6vw]': selectedCustomerCount === 9}]">
        <template v-if="settings.getValue('customers_names_to_be_viewed') == 'only_first_two_names'">
          {{
            activeCustomer && String(activeCustomer.customer?.name ?? '').split(" ").splice(0, 2).join(" ")
          }}
        </template>
        <template v-if="settings.getValue('customers_names_to_be_viewed') == 'full_name'">
          {{
            activeCustomer && String(activeCustomer.customer?.name ?? '')
          }}
        </template>
      </div>
    </div>

    <!-- Waiting Customers Section -->
    <div class="flex-1 flex flex-col p-[2vw] bg-gray-50/30" style="min-height: calc(100vh - 12vh - 24vh - 10vh);">
      <div class="text-[#A59764] font-bold text-[2.2vw] text-end mb-[2vh] shrink-0">
        في الإنتظار
      </div>
      <div dir="rtl" :class="gridClasses" class="gap-[1vh]" style="height: calc(100vh - 12vh - 20vh - 10vh - 8vw - 4vh);">
        <template v-for="(ac, index) in displayedCustomers" :key="index">
          <div class="flex items-center justify-start p-[0.8vh] border-s-2 border-[#A59764]/30">
            <span :class="textSizeClass" class="font-bold text-gray-800 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
               <template v-if="settings.getValue('customers_names_to_be_viewed') == 'only_first_two_names'">
                {{ index + 1 }}. {{
                  String(ac.customer?.name ?? '').split(" ").splice(0, 2).join(" ")
                }}
              </template>
              <template v-if="settings.getValue('customers_names_to_be_viewed') == 'full_name'">
                {{ index + 1 }}. {{
                  String(ac.customer?.name ?? '')
                }}
              </template>
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- Fixed Footer -->
    <div class="h-[10vh] bg-gray-50/30 shrink-0">
      <div class="h-full p-[1.5vw] flex justify-between items-center bg-[#D2DBE3]">
        <div class="flex gap-[1.5vw] items-center">
          <img :src="theckyLogo" class="h-[5vh] w-auto" alt="Thecky Logo">
          <!-- <img :src="theckyLogoAlt" class="h-[4vh] w-auto" alt="Thecky Logo Alt"> -->
        </div>
        <div class="text-[2.2vw] text-[#1D4C75] font-bold">
          {{ settings.getValue("customers_queue_footer") }}
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {useSettingsStore} from "@/stores/settingsStore";
import theckyLogo from "@/assets/images/footer.svg"
import theckyLogoAlt from "@/assets/images/queue-thecky-logo-alt.png"
import {onBeforeMount, onMounted, onUnmounted, ref, computed} from "vue";
import {CustomerQueue} from "@/views/customers/CustomersQueue.vue";
import {useHTTP} from "@/plugins/http";

const settings = useSettingsStore();
const http = useHTTP();
const activeCustomer = ref<CustomerQueue>();
const activeCustomers = ref<CustomerQueue[]>([]);
const selectedCustomerCount = ref(24); // Default to 16 customers
let timer: number | null = null;
const currentTime = ref('');

// Computed property to get the correct number of customers to display
const displayedCustomers = computed(() => {
  const maxToShow = selectedCustomerCount.value;
  return activeCustomers.value.slice(0, maxToShow);
});

// Computed property for dynamic grid classes
const gridClasses = computed(() => {
  switch (selectedCustomerCount.value) {
    case 9:
      return 'grid grid-cols-3 grid-rows-3 grid-flow-col auto-rows-fr';
    case 16:
      return 'grid grid-cols-4 grid-rows-4 grid-flow-col auto-rows-fr';
    case 24:
      return 'grid grid-cols-4 grid-rows-6 grid-flow-col auto-rows-fr';
    default:
      return 'grid grid-cols-4 grid-rows-4 grid-flow-col auto-rows-fr';
  }
});

// Computed property for responsive text sizing
const textSizeClass = computed(() => {
  switch (selectedCustomerCount.value) {
    case 9:
      return 'text-[3vw]';
    case 16:
      return 'text-[2vw]';
    case 24:
      return 'text-[2vw] lg:text-[1.8vw]';
    default:
      return 'text-[1.8vw]';
  }
});

function updateTime() {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

onBeforeMount(async () => {
  await settings.loadSettings();
  selectedCustomerCount.value = Number.parseInt(settings.getValue('customer_queue_monitor_screen_size') || '24');
})

onMounted(() => {
  load();
  timer = window.setInterval(load, 2000);
  updateTime();
  // Update time every second
  const timeInterval = setInterval(updateTime, 1000);
  onUnmounted(() => {
    clearInterval(timeInterval);
  });
})

const isFlashing = ref(false);
let previousCustomerId = '';

function playNotificationSound() {
  // const audio = new Audio(successbeep);
  // setTimeout(() => {
  //   audio.play();
  // }, 500)
}

function triggerFlashAnimation() {
  isFlashing.value = true;
  setTimeout(() => {
    isFlashing.value = false;
  }, 1500); // Animation duration
}

async function load() {
  let queueData = await http.get<Array<CustomerQueue>>('/customer/queue');
  queueData = queueData
      .filter((c: any) => c.status === 'active')
      .map((customer: any) => ({
        ...customer,
        createdAt: new Date(customer.createdAt)
      }));

  // Get enough data to fill the selected mode + 1 for active customer
  const totalNeeded = selectedCustomerCount.value + 1;
  queueData = queueData.slice(0, Math.max(totalNeeded, 50));

  if (queueData.length) {
    const newCustomer = queueData[0];
    if (previousCustomerId !== newCustomer.id) {
      previousCustomerId = newCustomer.id;
      triggerFlashAnimation();
      playNotificationSound();
    }
    activeCustomer.value = newCustomer;
  } else {
    activeCustomer.value = undefined;
  }

  activeCustomers.value = queueData.slice(1);
}
</script>

<style scoped>
.flash-animation {
  animation: flash 0.8s ease-in-out 3;
}

@keyframes flash {
  0%, 100% {
    background-color: rgb(187 247 208);
    border-color: rgb(134 239 172);
    transform: scale(1);
  }
  50% {
    background-color: #45944A;
    border-color: #45944A;
    color: white;
    transform: scale(1.05);
  }
}

/* Smooth transitions for better UX */
.transition-shadow {
  transition: box-shadow 0.2s ease;
}

/* Ensure proper grid sizing */
.min-h-0 {
  min-height: 0;
}

/* Force specific grid layouts */
.grid.grid-cols-4.grid-rows-6 {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  grid-template-rows: repeat(6, minmax(0, 1fr)) !important;
  grid-auto-flow: column !important;
  height: 100% !important;
}

.grid.grid-cols-3.grid-rows-3 {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  grid-template-rows: repeat(3, minmax(0, 1fr)) !important;
  grid-auto-flow: column !important;
  height: 100% !important;
}

.grid.grid-cols-4.grid-rows-4 {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  grid-template-rows: repeat(4, minmax(0, 1fr)) !important;
  grid-auto-flow: column !important;
  height: 100% !important;
}
</style>


